/**
 * Dicionários ÚNICOS de rótulo do painel: severidade, tipo de alerta, ação e entidade de auditoria.
 *
 * ⚠️ POR QUE EXISTE (auditoria de design de 11/10/2026, SEN-12/SEN-13): cada tela escrevia o seu mapa.
 * Severidade saía em três vocabulários e duas escritas: `MEDIA`/`CRITICA` SEM acento (Alertas, via
 * `severidade.toUpperCase()`), `critica`/`media` minúsculas e sem acento (Top ofensores, painel SLA),
 * "Médio/Crítico/Informativo" (cartões do Dashboard) e "Atenção/Alta/Crítica" (portal do cliente) — erro
 * de acento numa ferramenta que gera carta ao cliente, e "alta" e "crítica" com a MESMA cor vermelha na
 * lista de Alertas. Códigos internos viravam texto de interface: `ldap_aberto` na coluna Tipo (o mapa
 * local tinha 12 de ~23 tipos), `tftp_aberto` em "Aguardando cliente", `manutencao.estender` e
 * `manutencao ab000000…` na Auditoria.
 *
 * Regras que ficam:
 *  - O rótulo de tela NUNCA é o código. Código desconhecido cai num texto legível (`legivel`), e o código
 *    cru só aparece, em fonte mono e discreta, quando alguém precisa dele (detalhe, `title`).
 *  - Troca-se só a EXIBIÇÃO: o valor enviado à API, o filtro e o CSV continuam sendo o código.
 *  - Severidade tem quatro níveis com quatro rótulos e quatro tons distintos (crítica ≠ alta).
 *  - Ação/entidade nova de auditoria entra em `ROTULO_ACAO`/`ROTULO_ENTIDADE`; o teste
 *    (`rotulos.test.ts`) falha se uma ação conhecida não tiver rótulo.
 */
import { TIPO_ALERTA_LABEL, type TipoAlerta } from "./severidade.js";

// ─── Severidade ────────────────────────────────────────────────────────────

/** Do mais grave ao menos grave. */
export const ORDEM_SEVERIDADE = ["critica", "alta", "media", "baixa"] as const;
export type SeveridadeAlerta = (typeof ORDEM_SEVERIDADE)[number];

export const ROTULO_SEVERIDADE: Record<SeveridadeAlerta, string> = {
  critica: "Crítica",
  alta: "Alta",
  media: "Média",
  baixa: "Baixa",
};

/**
 * Texto legível a partir de um código desconhecido: troca `_` e `.` por espaço e põe a primeira maiúscula.
 * Não inventa acento nem tradução: serve de rede de segurança, e o rótulo certo entra nos dicionários.
 */
export function legivel(codigo: string): string {
  const t = codigo.replace(/[._]+/g, " ").trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : "—";
}

export function rotuloSeveridade(valor: string | null | undefined): string {
  if (!valor) return "—";
  return ROTULO_SEVERIDADE[valor.toLowerCase() as SeveridadeAlerta] ?? legivel(valor);
}

/**
 * Tom do selo. `critico` é o sólido (fundo vermelho cheio, texto branco): Crítica passa a ser VISUALMENTE
 * diferente de Alta (vermelho tingido), que antes tinham a mesma cor.
 */
export type TomSeveridade = "critico" | "danger" | "warning" | "ink";
export function tomSeveridade(valor: string | null | undefined): TomSeveridade {
  switch ((valor ?? "").toLowerCase()) {
    case "critica":
      return "critico";
    case "alta":
      return "danger";
    case "media":
      return "warning";
    default:
      return "ink";
  }
}

// ─── Tipo de alerta ────────────────────────────────────────────────────────

/** Versão curta, para cartões e rankings estreitos. Tipos fora daqui usam o rótulo completo. */
const ROTULO_TIPO_CURTO: Partial<Record<TipoAlerta, string>> = {
  tftp_aberto: "TFTP",
  dns_resolver_aberto: "DNS aberto",
  ntp_amplificador: "NTP",
  ssdp_aberto: "SSDP",
  snmp_aberto: "SNMP",
  mdns_aberto: "mDNS",
  coap_aberto: "CoAP",
  chargen_aberto: "CharGen",
  netbios_aberto: "NetBIOS",
  portmap_aberto: "Portmap",
  smtp_open_relay: "SMTP relay",
  ldap_aberto: "LDAP",
  servidor_ssh_vulneravel: "SSH",
  ddos_origem: "DDoS",
  abuse_report: "Abuse",
  ataque_externo_reportado: "Ataque ext.",
  botnet_malware: "Malware",
  porta_admin_exposta: "Porta admin",
  servico_exposto: "Serviço exposto",
};

export function rotuloTipoAlerta(tipo: string | null | undefined, curto = false): string {
  if (!tipo) return "—";
  if (curto) {
    const c = ROTULO_TIPO_CURTO[tipo as TipoAlerta];
    if (c) return c;
  }
  return (TIPO_ALERTA_LABEL as Record<string, string>)[tipo] ?? legivel(tipo);
}

// ─── Auditoria ─────────────────────────────────────────────────────────────

export const ROTULO_ENTIDADE: Record<string, string> = {
  alertas: "Alerta",
  automation_job: "Pedido de circuito",
  carta: "Aviso emitido",
  changelog_entradas: "ChangeLog",
  cliente: "Cliente",
  clientes: "Cliente",
  clientes_ips: "IP de cliente",
  clientes_subnets: "Vínculo de rede",
  configuracao: "Configuração",
  dns: "DNS",
  dns_prefixo: "Prefixo DNS",
  dns_ptr: "Registro PTR",
  dns_record: "Registro DNS",
  dns_zona: "Zona DNS",
  email_messages: "E-mail recebido",
  forense_busca: "Consulta forense",
  geofeed_entradas: "Entrada do Geofeed",
  incidente: "Incidente",
  incidentes: "Incidente",
  incidente_ddos_inbound: "Incidente DDoS",
  infra_cache: "Cache de infraestrutura",
  infra_device: "Equipamento",
  infra_marca: "Marca de equipamento",
  infra_modelo: "Modelo de equipamento",
  ioc: "Indicador (IOC)",
  manutencao: "Manutenção",
  oficios_judiciais: "Ofício judicial",
  phpipam: "phpIPAM",
  phpipam_address: "Endereço no phpIPAM",
  servico_circuito: "Circuito",
  sessoes_usuario: "Sessão de usuário",
  sistema: "Sistema",
  trocas_prefixo: "Troca de prefixo",
  usuarios: "Usuário",
  verificacoes_incidente: "Verificação de resolução",
  vinculos: "Vínculo",
};

export function rotuloEntidade(tipo: string | null | undefined): string {
  if (!tipo) return "—";
  return ROTULO_ENTIDADE[tipo] ?? legivel(tipo);
}

/** Ações de auditoria conhecidas. A chave é o código gravado em `auditoria.acao`. */
export const ROTULO_ACAO: Record<string, string> = {
  "alerta.enviar_aviso": "Enviou aviso do alerta",
  "alerta.marcar_resolvido_manual": "Marcou alerta como resolvido",
  "alerta.reprocessar": "Reprocessou alerta",
  "alerta.reprocessar_bulk": "Reprocessou alertas em lote",
  "alerta.verificar_agora": "Verificou alerta agora",
  "carta.aprovar": "Aprovou aviso",
  "carta.aprovar_em_massa": "Aprovou avisos em lote",
  "carta.cancelar": "Cancelou aviso",
  "carta.enviar_agora": "Enviou aviso agora",
  "carta.enviar_em_massa_resultado": "Enviou avisos em lote",
  "carta.reenviar": "Reenviou aviso",
  "carta.reenviar_falhou": "Falha ao reenviar aviso",
  "changelog.apagar": "Apagou entrada do ChangeLog",
  "changelog.criar": "Criou entrada do ChangeLog",
  "changelog.editar": "Editou entrada do ChangeLog",
  "circuito.criar": "Criou circuito",
  "circuito.excluir": "Excluiu circuito",
  "circuito.job_aprovado": "Aprovou pedido de circuito",
  "circuito.job_cancelado": "Cancelou pedido de circuito",
  "circuito.job_criado": "Criou pedido de circuito",
  "circuito.reconciliar": "Reconciliou circuito",
  "circuito.tipo_liberado": "Liberou tipo de pedido",
  "cliente.atualizar": "Atualizou cliente",
  "cliente.contato.remover": "Removeu contato do cliente",
  "cliente.criar_do_ixc": "Criou cliente a partir do IXC",
  "cliente.criar_do_ixc_via_circuito": "Criou cliente do IXC pelo circuito",
  "cliente.email.adicionar": "Adicionou e-mail do cliente",
  "cliente.email.editar": "Editou e-mail do cliente",
  "cliente.email.remover": "Removeu e-mail do cliente",
  "cliente.email_boas_vindas": "Enviou e-mail de boas-vindas",
  "cliente.esquecer_lgpd": "Eliminou dados do cliente (LGPD)",
  "cliente.importar_ixc": "Importou cliente do IXC",
  "cliente.ip_criar": "Cadastrou IP do cliente",
  "cliente.ip_encerrar_vigencia": "Encerrou vigência de IP",
  "cliente.notificar_manual": "Notificou cliente manualmente",
  "cliente.reatribuir_alertas": "Reatribuiu alertas do cliente",
  "cliente.reenviar_boas_vindas": "Reenviou boas-vindas",
  "cliente.reprocessar_pendentes": "Reprocessou pendências do cliente",
  "cliente.score.recalcular": "Recalculou pontuação do cliente",
  "cliente.subnet_criar": "Vinculou rede ao cliente",
  "cliente.sync_phpipam": "Sincronizou cliente com o phpIPAM",
  "cliente.telefone.adicionar": "Adicionou telefone do cliente",
  "cliente.telefone.editar": "Editou telefone do cliente",
  "cliente.telefone.remover": "Removeu telefone do cliente",
  "configuracao.atualizar": "Alterou configuração",
  "ddos_inbound.arquivar": "Arquivou incidente DDoS",
  "ddos_inbound.relatorio": "Gerou relatório DDoS",
  "dns.add_record": "Criou registro DNS",
  "dns.create_reverse_zones": "Criou zonas reversas",
  "dns.create_zone": "Criou zona DNS",
  "dns.delete_ptr": "Apagou registro PTR",
  "dns.delete_record": "Apagou registro DNS",
  "dns.delete_zone": "Apagou zona DNS",
  "dns.reload": "Recarregou o DNS",
  "email.reprocessar": "Reprocessou e-mail recebido",
  "email.reprocessar_em_massa": "Reprocessou e-mails em lote",
  "equipamento.configurar": "Configurou equipamento",
  "forense.assinante": "Consulta forense: assinante",
  "forense.cliente_acessos": "Consulta forense: acessos do cliente",
  "forense.cliente_resumo": "Consulta forense: resumo do cliente",
  "forense.destino": "Consulta forense: destino",
  "forense.mac": "Consulta forense: MAC",
  "forense.nat_resumo": "Consulta forense: resumo do NAT",
  "forense.privada": "Consulta forense: IP privado",
  "forense.publica": "Consulta forense: IP público",
  "forense.radius": "Consulta forense: RADIUS",
  "geofeed.remover_entrada": "Removeu entrada do Geofeed",
  "incidente.nivel_procedural.override": "Mudou o nível procedural do incidente",
  "incidente.nota_apagada": "Apagou nota do incidente",
  "incidente.nota_criada": "Criou nota do incidente",
  "incidente.responsavel": "Mudou o responsável do incidente",
  "incidente.status_followup": "Mudou o acompanhamento do incidente",
  "infra.cache_invalidar": "Limpou cache de infraestrutura",
  "infra.device_criar": "Criou equipamento",
  "infra.device_criar_falha": "Falha ao criar equipamento",
  "infra.device_editar": "Editou equipamento",
  "infra.device_editar_falha": "Falha ao editar equipamento",
  "infra.marca_criar": "Criou marca de equipamento",
  "infra.marca_editar": "Editou marca de equipamento",
  "infra.modelo_criar": "Criou modelo de equipamento",
  "infra.modelo_editar": "Editou modelo de equipamento",
  "infra.phpipam_address_criar": "Criou endereço no phpIPAM",
  "ioc.atualizar": "Atualizou indicador (IOC)",
  "ioc.bulk": "Alterou indicadores (IOC) em lote",
  "ioc.criar": "Criou indicador (IOC)",
  "ioc.inativar": "Inativou indicador (IOC)",
  "lgpd.purga_executada": "Purga LGPD executada",
  "manutencao.cancelar": "Cancelou manutenção",
  "manutencao.concluir": "Concluiu manutenção",
  "manutencao.criar": "Criou manutenção",
  "manutencao.editar": "Editou manutenção",
  "manutencao.estender": "Estendeu manutenção",
  "manutencao.excluir_rascunho": "Excluiu rascunho de manutenção",
  "manutencao.publicar": "Publicou manutenção",
  "oficio.arquivar": "Arquivou ofício",
  "oficio.buscar_conclusiva": "Busca conclusiva do ofício",
  "oficio.buscar_sem_porta": "Busca do ofício sem porta",
  "oficio.cancelar": "Cancelou ofício",
  "oficio.criar": "Criou ofício",
  "oficio.download": "Baixou documento do ofício",
  "oficio.editar": "Editou ofício",
  "oficio.exportar": "Exportou ofício",
  "oficio.responder": "Respondeu ofício",
  "sessao.encerrar_remoto": "Encerrou sessão remotamente",
  "troca.cancelar": "Cancelou troca de prefixo",
  "troca.confirmar": "Confirmou troca de prefixo",
  "troca.criar": "Criou troca de prefixo",
  "troca.editar": "Editou troca de prefixo",
  "troca.enviar": "Enviou aviso da troca de prefixo",
  "troca.enviar_falhou": "Falha ao enviar aviso da troca",
  "troca.executar": "Executou troca de prefixo",
  "troca.mensagem": "Mensagem da troca de prefixo",
  "usuario.criar": "Criou usuário",
  "usuario.recriar": "Reativou usuário",
  "usuario.deletar": "Removeu usuário",
  "usuario.editar": "Editou usuário",
  "usuario.email_boas_vindas": "Enviou boas-vindas ao usuário",
  "usuario.reenviar_boas_vindas": "Reenviou boas-vindas ao usuário",
  "verificacao.aplicar_resultado": "Aplicou resultado da verificação",
  "verificacao.evidencia_baixada": "Baixou evidência da verificação",
  "verificacao.resolver_manual": "Resolveu verificação manualmente",
  "vinculo.criar": "Criou vínculo",
  "vinculo.deletar": "Removeu vínculo",
  "vinculo.limpar_phpipam_customer_id": "Limpou cliente no phpIPAM",
};

const VERBOS_GENERICOS: Record<string, string> = {
  criar: "Criou",
  editar: "Editou",
  atualizar: "Atualizou",
  deletar: "Removeu",
  excluir: "Excluiu",
  remover: "Removeu",
  cancelar: "Cancelou",
  aprovar: "Aprovou",
  reenviar: "Reenviou",
  enviar: "Enviou",
};

/**
 * Rótulo de uma ação de auditoria. Conhecida: o texto do dicionário. Desconhecida (ação nova que
 * ninguém cadastrou): "Entidade: verbo" legível, nunca `manutencao.estender` cru como rótulo.
 */
export function rotuloAcaoAuditoria(acao: string | null | undefined): string {
  if (!acao) return "—";
  const direto = ROTULO_ACAO[acao];
  if (direto) return direto;
  const [dominio, ...resto] = acao.split(".");
  const verbo = resto.join(".");
  if (!verbo) return legivel(acao);
  const generico = VERBOS_GENERICOS[verbo];
  const alvo = ROTULO_ENTIDADE[dominio!] ?? legivel(dominio!);
  return generico ? `${generico} ${alvo.toLowerCase()}` : `${alvo}: ${legivel(verbo).toLowerCase()}`;
}

// ─── Faixa de pontuação do cliente ─────────────────────────────────────────

export type TomFaixaScore = "success" | "warning" | "danger";

/**
 * Faixa da pontuação (0 a 100) do cliente: UMA régua para Top ofensores e o detalhe do cliente.
 * Verde para Excelente e Bom, âmbar SÓ para Atenção e vermelho para Crítico (DO-45, 11/10/2026: "Bom" saía
 * em amarelo-escuro e "Atenção" em laranja, dois tons quase iguais, e o amarelo é a cor de "atenção").
 */
export function faixaScore(score: number): { rotulo: string; tom: TomFaixaScore } {
  if (score >= 85) return { rotulo: "Excelente", tom: "success" };
  if (score >= 65) return { rotulo: "Bom", tom: "success" };
  if (score >= 40) return { rotulo: "Atenção", tom: "warning" };
  return { rotulo: "Crítico", tom: "danger" };
}

// ─── "O quê" de um evento de auditoria ─────────────────────────────────────

/** Instante ISO -> "09/10 14:00" na hora da Bahia (curto, para caber numa linha da tabela). */
function curto(iso: unknown): string | null {
  if (typeof iso !== "string") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d
    .toLocaleString("pt-BR", { timeZone: "America/Bahia", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
    .replace(", ", " ");
}

/**
 * Frase curta do que o evento FEZ, montada só do `metadata_json` (auditoria 11/10/2026, SEN-14: a tabela
 * mostrava `manutencao` + `ab000000…` e o código, o novo fim e o antes/depois só apareciam em JSON cru ao
 * clicar). Nunca devolve texto livre do cliente: os metadados de manutenção já não o guardam (§17.10 item 7).
 * Evento antigo, sem `codigo`, devolve `null` e a tela cai no id curto — nada é inventado.
 */
export function resumoDoEvento(acao: string, metadataJson: string | null | undefined): string | null {
  if (!metadataJson) return null;
  let m: Record<string, unknown>;
  try {
    const v = JSON.parse(metadataJson) as unknown;
    if (!v || typeof v !== "object" || Array.isArray(v)) return null;
    m = v as Record<string, unknown>;
  } catch {
    return null;
  }
  const codigo = typeof m.codigo === "string" ? m.codigo : null;
  if (!codigo) return null;
  switch (acao) {
    case "manutencao.estender": {
      const novo = curto(m.fim_novo);
      const antes = curto(m.fim_anterior);
      return `${codigo}: término ${antes ? `de ${antes} ` : ""}para ${novo ?? "um novo horário"}`;
    }
    case "manutencao.concluir": {
      const fim = curto(m.fim_novo);
      return `${codigo}: encerrada${fim ? ` às ${fim}` : ""}`;
    }
    case "manutencao.cancelar":
      return `${codigo}: cancelada${m.com_motivo === true ? " (com motivo)" : ""}`;
    case "manutencao.publicar": {
      const a = m.tipo_anterior;
      const p = m.tipo_publicado;
      return a && p && a !== p ? `${codigo}: publicada como ${String(p).toLowerCase()} (era ${String(a).toLowerCase()})` : `${codigo}: publicada`;
    }
    case "manutencao.editar":
      return Array.isArray(m.campos_alterados) && m.campos_alterados.length
        ? `${codigo}: alterou ${(m.campos_alterados as unknown[]).map(String).join(", ")}`
        : `${codigo}: editada`;
    case "manutencao.criar": {
      const i = curto(m.inicio);
      return `${codigo}: criada${i ? ` para ${i}` : ""}`;
    }
    case "manutencao.excluir_rascunho":
      return `${codigo}: rascunho excluído`;
    default:
      return codigo;
  }
}
